import { HookForm, OsiInfoWidgetViewModel } from '@shared/types/mobx/widgets/OsiInfoWidget';
import { inject, injectable } from 'inversify';
import { DictionaryModule } from '@mobx/services/dictionaries/model/DictionaryModule';
import { autorun, makeAutoObservable } from 'mobx';
import { get as _get, clone as _clone, isEmpty } from 'lodash';
import { formatPhone, reformatPhone } from '@shared/utils/helpers/formatString';
import { mockHouseStateCode } from '@shared/constants/houseStateCode';
import api from '@app/api';
import { notifications } from '@shared/utils/notifications';
import logger from 'js-logger';
import { OsiInfoValues } from '@shared/types/osi';
import { type RolesService, RolesServiceIdentifier } from '@shared/types/mobx/services/roles';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { initialFormValues } from '../config';

@injectable()
export class ViewModel implements OsiInfoWidgetViewModel {
  formValues = initialFormValues;

  hookForm: HookForm = null;

  constructor(
    @inject(OsiServiceIdentifier) private osiModule: OsiService,
    private dictionaryModule: DictionaryModule,
    @inject(RolesServiceIdentifier) private rolesModule: RolesService
  ) {
    makeAutoObservable(this);

    autorun(() => {
      this.fillFormValues();
    });
  }

  get houseStates() {
    return this.dictionaryModule.houseStates;
  }

  get lockedFields() {
    const hasRoles = this.rolesModule.isHasAdminRole || this.rolesModule.isHasOperatorRole;

    return !hasRoles ? ['name', 'idn', 'fio', 'floors'] : [];
  }

  get docs() {
    return this.osiModule.osiDocs || [];
  }

  get osiUnionTypeName() {
    return {
      Ru: this.osiModule.osiInfo?.unionTypeRu ?? '',
      Kz: this.osiModule.osiInfo?.unionTypeKz ?? ''
    };
  }

  fillFormValues = () => {
    if (!this.osiModule.osiInfo) return;

    // set values from osi info
    const formKeys = Object.keys(this.formValues);

    formKeys.forEach((key) => {
      if (key === 'phone') {
        (this.formValues[key as keyof OsiInfoValues] as any) = formatPhone(this.osiModule.osiInfo?.[key]);
        return;
      }

      if (key === 'constructionMaterial' || key === 'constructionYear') {
        return;
      }

      // OSI-821: null/undefined → '' (раньше String(null) давал строку "null" и ломал валидацию email/floors)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const value = this.osiModule.osiInfo?.[key];
      (this.formValues[key as keyof OsiInfoValues] as any) = value === null || value === undefined ? '' : String(value);
    });

    this.hookForm?.reset(this.formValues);
    this.hookForm?.trigger();
  };

  setHookForm = (hookForm: HookForm) => {
    this.hookForm = hookForm;
  };

  submitForm = async (data: any) => {
    const payload = _clone(data);
    payload.phone = reformatPhone(payload.phone);
    // OSI-821: пусто → null (бэк: Floors [Range(1,100)] int?, 0 даёт 400); email пусто → null, чтобы не затирать null пустой строкой
    payload.floors = !payload.floors ? null : parseInt(payload.floors, 10);
    payload.email = !payload.email ? null : payload.email;
    // TODO: need to remove houseStateCode from endpoint
    // OSI-821: берём текущее состояние дома, мок 'NORMAL' только как fallback — иначе «Сохранить» сбрасывал DECREPIT и др. в NORMAL
    payload.houseStateCode = this.osiModule.osiInfo?.houseStateCode ?? mockHouseStateCode;
    // TODO: get all info from osi module
    // OSI-821: поля OsiRequest, которые форма НЕ редактирует, берём из текущего состояния ОСИ (osiInfo = GET /api/Osi/{id}).
    // Бэк (OsiSvc.UpdateOsi → AutoMapper Map(request, osi)) перезаписывает в БД всё, что есть в OsiRequest:
    // отсутствующее в payload поле приходит как null/false/default и затирает значение в БД.
    const { osiInfo } = this.osiModule;
    payload.address = osiInfo?.address;
    // OSI-821: раньше не отправлялся → геттер OsiRequest.AddressKz (field ?? Address) записывал в address_kz русский адрес
    payload.addressKz = osiInfo?.addressKz;
    payload.coefUnlivingArea = osiInfo?.coefUnlivingArea;
    payload.wreckage = false;
    payload.unionTypeId = osiInfo?.unionTypeId;
    // OSI-821: раньше не отправлялись → в БД затирались в null (год/материал постройки, кол-во помещений)
    payload.constructionYear = osiInfo?.constructionYear ?? null;
    payload.constructionMaterial = osiInfo?.constructionMaterial ?? null;
    payload.apartCount = osiInfo?.apartCount ?? null;
    // OSI-821: раньше не отправлялся → bool CreateFine = false, т.е. «Сохранить» выключал начисление пени
    payload.createFine = osiInfo?.createFine ?? false;
    // OSI-821: раньше уходила строка из fillFormValues (String(null) → '' при пустом значении); берём как есть
    payload.registrationType = osiInfo?.registrationType ?? null;

    // OSI-821: раньше хардкод null затирал признаки в БД. Переключатели удалены из UI (коммит 4042ee8e
    // «remove facilities»), а значения формы после fillFormValues — строки ('true'/'false'/''), которые бэк
    // (System.Text.Json, bool?) не принимает. Поэтому берём исходные boolean/null из osiInfo.
    payload.personalHeating = osiInfo?.personalHeating ?? null;
    payload.personalHotWater = osiInfo?.personalHotWater ?? null;
    payload.personalElectricPower = osiInfo?.personalElectricPower ?? null;
    payload.gasified = osiInfo?.gasified ?? null;

    try {
      await api.OsiUpdate(this.osiModule.osiInfo?.id, payload);
      await this.osiModule.loadOsiInfo();
      notifications.success('common:saved');
    } catch (err: any) {
      const errors = _get(err, ['response', 'data', 'errors']);
      if (isEmpty(errors)) {
        notifications.error(err.toString());
      }
      logger.error(errors);
    }
  };
}
